import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _omit from 'lodash/omit';
import { FlexMixin } from '@zillow/constellation';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { borderRadiusMixin } from '../../mixins/borderRadiusMixin.js';
import { GroupMixin } from '../../mixins/components/GroupMixin.js';
import { negativeMarginMixin } from '../../mixins/negativeMarginMixin.js';
import customPropTypes from '../../utils/custom-prop-types.js';
import withPreload from '../../utils/with-preload.js';
import Picture from '../Picture/Picture.js';
import Source from '../Source/Source.js';
import GroupBackgroundImage from './GroupBackgroundImage.js';
import GroupBackgroundVideo from './GroupBackgroundVideo.js';

var _excluded = ["children", "backgroundImage", "backgroundImageVisible", "backgroundVideo", "backgroundVideoVisible", "backgroundPosition", "backgroundSize", "backgroundVideoPosition", "backgroundVideoSize", "loading", "backgroundImagePreloaded", "isEditor"];
var GroupBackgroundImagePreload = withPreload(GroupBackgroundImage);
var GroupBackgroundVideoPreload = withPreload(GroupBackgroundVideo, {
  as: 'video'
});
var TRANSPARENT_PIXEL_SOURCE = {
  src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
  height: 0,
  width: 0,
  loading: 'lazy'
};
var GroupContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-50qjnk-0"
})(["", " ", " ", " ", ""], GroupMixin, borderRadiusMixin, function (props) {
  return FlexMixin(Object.assign({
    // "visible" is a new prop. If not yet present, then default to
    // old way of always showing.
    display: props.visible ? {
      default: props.visible.default ? 'flex' : 'none',
      lg_lte: props.visible.lg_lte ? 'flex' : 'none',
      sm_lte: props.visible.sm_lte ? 'flex' : 'none'
    } : 'flex',
    flexDirection: 'column'
  }, props));
}, negativeMarginMixin);
var Group = function Group(_ref) {
  var children = _ref.children,
    backgroundImage = _ref.backgroundImage,
    backgroundImageVisible = _ref.backgroundImageVisible,
    backgroundVideo = _ref.backgroundVideo,
    backgroundVideoVisible = _ref.backgroundVideoVisible,
    backgroundPosition = _ref.backgroundPosition,
    backgroundSize = _ref.backgroundSize,
    backgroundVideoPosition = _ref.backgroundVideoPosition,
    backgroundVideoSize = _ref.backgroundVideoSize,
    loading = _ref.loading,
    backgroundImagePreloaded = _ref.backgroundImagePreloaded,
    isEditor = _ref.isEditor,
    rest = _objectWithoutProperties(_ref, _excluded);
  var GroupBackgroundVideoComponent = isEditor ? GroupBackgroundVideo : GroupBackgroundVideoPreload;
  return /*#__PURE__*/React.createElement(GroupContainer, rest, renderBackgroundImage({
    backgroundImage: backgroundImage,
    backgroundImageVisible: backgroundImageVisible,
    backgroundPosition: backgroundPosition,
    backgroundSize: backgroundSize,
    loading: loading,
    preloaded: backgroundImagePreloaded
  }), /*#__PURE__*/React.createElement(GroupBackgroundVideoComponent, {
    source: backgroundVideo,
    visible: backgroundVideoVisible,
    size: backgroundVideoSize,
    position: backgroundVideoPosition
    // For <GroupBackgroundVideoPreload>
    ,
    loading: loading,
    isEditor: isEditor
  }), children);
};
function renderBackgroundImage(_ref2) {
  var _backgroundImage$defa;
  var backgroundImage = _ref2.backgroundImage,
    backgroundImageVisible = _ref2.backgroundImageVisible,
    backgroundPosition = _ref2.backgroundPosition,
    backgroundSize = _ref2.backgroundSize,
    loading = _ref2.loading,
    preloaded = _ref2.preloaded;
  // Must always have a default source as if only dont set the source when
  // "backgroundImage.default" is false, then it will not cascade to tablet.
  var defaultSource = backgroundImage !== null && backgroundImage !== void 0 && (_backgroundImage$defa = backgroundImage.default) !== null && _backgroundImage$defa !== void 0 && _backgroundImage$defa.src ? backgroundImage.default : TRANSPARENT_PIXEL_SOURCE;
  var lgLteSource = backgroundImageVisible !== null && backgroundImageVisible !== void 0 && backgroundImageVisible.lg_lte ? backgroundImage === null || backgroundImage === void 0 ? void 0 : backgroundImage.lg_lte : undefined;
  var smLteSource = backgroundImageVisible !== null && backgroundImageVisible !== void 0 && backgroundImageVisible.sm_lte ? backgroundImage === null || backgroundImage === void 0 ? void 0 : backgroundImage.sm_lte : undefined;
  var hasBackgroundImage = [defaultSource, lgLteSource, smLteSource].filter(Boolean).some(function (source) {
    return source !== TRANSPARENT_PIXEL_SOURCE;
  });
  if (!hasBackgroundImage) {
    return null;
  }
  var GroupBackgroundImageComponent = preloaded ? GroupBackgroundImagePreload : GroupBackgroundImage;
  return /*#__PURE__*/React.createElement(Picture, null, smLteSource && /*#__PURE__*/React.createElement(Source, _extends({
    srcSet: smLteSource.src,
    media: "sm_lte",
    loading: loading
  }, _omit(smLteSource, 'src'))), lgLteSource && /*#__PURE__*/React.createElement(Source, _extends({
    srcSet: lgLteSource.src,
    media: "lg_lte",
    loading: loading
  }, _omit(lgLteSource, 'src'))), /*#__PURE__*/React.createElement(GroupBackgroundImageComponent, _extends({
    src: defaultSource.src,
    backgroundPosition: backgroundPosition,
    backgroundSize: backgroundSize,
    backgroundImageVisible: backgroundImageVisible,
    loading: loading
  }, _omit(defaultSource, 'src'))));
}
Group.propTypes = process.env.NODE_ENV !== "production" ? {
  as: PropTypes.string,
  alignItems: customPropTypes.inlineMediaQuery(PropTypes.oneOf(['stretch', 'center', 'flex-start', 'flex-end', 'baseline'])),
  backgroundColor: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundImage: customPropTypes.inlineMediaQuery(PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number
  })),
  backgroundVideo: customPropTypes.inlineMediaQuery(PropTypes.shape({
    src: PropTypes.string
  })),
  backgroundImagePreloaded: PropTypes.bool,
  isEditor: PropTypes.bool,
  backgroundPosition: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundSize: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundVideoPosition: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundVideoSize: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundImageVisible: customPropTypes.inlineMediaQuery(PropTypes.bool),
  backgroundVideoVisible: customPropTypes.inlineMediaQuery(PropTypes.bool),
  fontColor: customPropTypes.inlineMediaQuery(PropTypes.string),
  justifyContent: customPropTypes.inlineMediaQuery(PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly', 'initial', 'inherit'])),
  maxWidth: customPropTypes.inlineMediaQuery(PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'full'])),
  minHeight: customPropTypes.inlineMediaQuery(PropTypes.oneOf(['none', 'short', 'tall', 'fluid', 'pixel'])),
  minHeightPixel: customPropTypes.inlineMediaQuery(PropTypes.string),
  padding: customPropTypes.inlineMediaQuery(PropTypes.shape({
    paddingTop: PropTypes.string,
    paddingRight: PropTypes.string,
    paddingLeft: PropTypes.string,
    paddingBottom: PropTypes.string
  })),
  borderRadius: customPropTypes.inlineMediaQuery(PropTypes.shape({
    borderRadiusTopLeft: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    borderRadiusTopRight: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    borderRadiusBottomLeft: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    borderRadiusBottomRight: PropTypes.oneOf([PropTypes.string, PropTypes.number])
  }))
} : {};
Group.defaultProps = {
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 'xl',
  minHeight: 'none',
  minHeightPixel: '0px',
  isEditor: false
};
/** @component */
var Group$1 = Group;

export { Group$1 as default };
